<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import { MapSource, MapLayer } from '@/components/map'
import { getBaseUrl } from '@/utils/url'

const store = useStore()

const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  mosaics: {
    type: Array, // Only monthly array
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  },
  options: {
    type: Object,
    default: () => ({})
  }
})

const host = getBaseUrl()

const monthNames = [
  'January', 'February', 'March',
  'April', 'May', 'June',
  'July', 'August', 'September',
  'October', 'November', 'December'
]

const monthlyAvailableMonthsByYear = ref({})
const monthlyYearOptions = ref([])
const monthlySelectedYear = ref(null)
const monthlySelectedMonth = ref(null)
const monthlyLastLayedId = ref(null)

onMounted(() => {
  props.mosaics.forEach(m => {
    const date = new Date(m.first_acquired)
    const y = date.getUTCFullYear()
    const mo = date.getUTCMonth() + 1 // 1..12
    if (!monthlyAvailableMonthsByYear.value[y]) {
      monthlyAvailableMonthsByYear.value[y] = new Set()
    }
    monthlyAvailableMonthsByYear.value[y].add(mo)
  })

  const numericYears = Object.keys(monthlyAvailableMonthsByYear.value)
    .map(y => parseInt(y, 10))
    .sort((a, b) => a - b)

  monthlyYearOptions.value = numericYears.map(y => String(y))

  // Default to the first mosaic if it exists
  if (props.mosaics.length) {
    const defaultMosaic = props.mosaics[0]
    const defaultDate = new Date(defaultMosaic.first_acquired)
    monthlySelectedYear.value = String(defaultDate.getUTCFullYear())
    monthlySelectedMonth.value = String(defaultDate.getUTCMonth() + 1)
    monthlyLastLayedId.value = `planet-${defaultMosaic.name}-layer`
  }
})

const monthOptionsForSelectedYear = computed(() => {
  if (!monthlySelectedYear.value) return []
  const monthsSet = monthlyAvailableMonthsByYear.value[Number(monthlySelectedYear.value)]
  if (!monthsSet) return []
  return [...monthsSet].sort((a, b) => a - b).map(m => String(m))
})

const monthlySelectedMosaic = computed(() => {
  const selYear = Number(monthlySelectedYear.value)
  const selMonth = Number(monthlySelectedMonth.value)

  return props.mosaics.find(m => {
    const date = new Date(m.first_acquired)
    return (
      date.getUTCFullYear() === selYear &&
      (date.getUTCMonth() + 1) === selMonth
    )
  }) || props.mosaics[0]
})

watch([monthlySelectedYear, monthlySelectedMonth], () => {
  if (monthlySelectedMosaic.value) {
    monthlyLastLayedId.value = `planet-${monthlySelectedMosaic.value.name}-layer`
  }
})
</script>

<template>
  <div v-if="mosaics.length">
    <div class="pointer-events-auto">
      <div class="flex space-x-1 -mr-1">
        <Select
          v-if="monthlyYearOptions.length"
          v-model="monthlySelectedYear"
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a year" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="(year, index) in monthlyYearOptions"
                :key="index"
                :value="year"
              >
                {{ year }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <Select
          v-if="monthOptionsForSelectedYear.length"
          v-model="monthlySelectedMonth"
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a month" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="(month, index) in monthOptionsForSelectedYear"
                :key="index"
                :value="month"
              >
                {{ monthNames[Number(month) - 1] }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div v-if="map">
      <div v-for="mosaic in mosaics" :key="mosaic.name">
        <MapSource
          v-if="monthlyLastLayedId === `planet-${mosaic.name}-layer`"
          :map="map"
          type="raster"
          :id="`${layerId}-planet-${mosaic.name}-source`"
          :options="{
            tiles: [`${host}/tulum/tiles/planetscope/${orgId}/${mosaic.name}/{z}/{x}/{y}.png`],
            tileSize: 256
          }"
        >
          <MapLayer
            :map="map"
            :id="`${layerId}-planet-${mosaic.name}-layer`"
            type="raster"
            :source="`${layerId}-planet-${mosaic.name}-source`"
            :before-id="beforeId"
            :group-id="groupId"
            :options="options"
          />
        </MapSource>
      </div>
    </div>
  </div>
</template>
